import api from './apiService';

export interface TimeSlotResponse {
  public_id: string;
  day_of_week: string;
  start_time: string;
  end_time: string;
  duration_minutes: number;
  base_price: number;
  slot_order: number;
  dp_price: number;
  is_available: boolean;
}

export interface FieldShiftResponse {
  day_of_week: string;
  start_time: string;
  end_time: string;
  slot_duration_minutes: number;
}

export interface FieldDetailResponse {
  capacity: number;
  description: string;
  slug: string;
  images: {
    images: string[];
  };
  is_dp_enabled: boolean;
  is_active?: boolean;
  name: string;
  physical_field_name: string;
  physical_field_slug: string;
  /** > 1 when several facilities.fields share the same physical_field (booking share ON). */
  shared_field_count?: number;
  sport_category_slug: string;
  type: string;
  surface_type: string;
  arena_slug: string;
  arena_name: string;
  field_shifts?: FieldShiftResponse[];
  time_slots: TimeSlotResponse[];
  config?: {
    single_selection?: boolean;
    break_times?: Array<{
      start_time: string;
      end_time: string;
    }>;
    interval_minutes?: number;
  };
}


export interface FieldDetailApiResponse {
  success: boolean;
  message: string;
  data: FieldDetailResponse;
}

export interface UpdateFieldPayload {
  name: string;
  description: string;
  capacity: number;
  sport_category_slug: string;
  type: string;
  surface_type: string;
  physical_field_slug?: string;
  time_slots: Array<{
    public_id?: string;
    day_of_week: string;
    start_time: string;
    end_time: string;
    duration_minutes: number;
    base_price: number;
    slot_order: number;
    dp_price: number;
    is_available: boolean;
  }>;
  keep_images: string[];
  config?: {
    single_selection?: boolean;
    break_times?: Array<{
      start_time: string;
      end_time: string;
    }>;
    interval_minutes?: number;
  };
  is_active?: boolean;
}

class FieldDetailApiService {
  async getFieldDetail(arenaSlug: string, fieldSlug: string): Promise<FieldDetailResponse> {
    const response = await api.get<FieldDetailApiResponse>(
      `/arenas/${arenaSlug}/fields/${fieldSlug}/detail-with-timeslots`
    );

    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to fetch field detail');
    }
  }

  async updateField(
    arenaSlug: string,
    fieldSlug: string,
    payload: UpdateFieldPayload,
    newImages: File[] = []
  ): Promise<{ success: boolean; message: string }> {
    try {
      const formData = new FormData();

      // Append scalar fields
      formData.append('name', payload.name);
      formData.append('description', payload.description);
      formData.append('capacity', String(payload.capacity));
      formData.append('sport_category_slug', payload.sport_category_slug);
      formData.append('type', payload.type);
      formData.append('surface_type', payload.surface_type);

      // Add physical_field_slug only if present
      if (payload.physical_field_slug) {
        formData.append('physical_field_slug', payload.physical_field_slug);
      }

      // time_slots must be serialized as JSON string
      formData.append('time_slots', JSON.stringify(payload.time_slots));

      // Add keep_images as JSON string
      formData.append('keep_images', JSON.stringify(payload.keep_images));

      formData.append('config', JSON.stringify(payload.config || {}));
      if (payload.is_active !== undefined) {
        formData.append('is_active', String(payload.is_active));
      }

      // Add new images
      newImages.forEach((file) => {
        formData.append('images', file);
      });

      const response = await api.put(
        `/arenas/${arenaSlug}/fields/${fieldSlug}/full-with-images`,
        formData
      );

      return {
        success: response.data.success || response.status === 200,
        message: response.data.message || 'Field updated successfully',
      };
    } catch (error: any) {
      console.error('Error updating field:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to update field');
    }
  }

  async deleteField(
    arenaSlug: string,
    fieldSlug: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.delete(`/arenas/${arenaSlug}/field/${fieldSlug}`);

      return {
        success: response.status === 200,
        message: response.data?.message || 'Field deleted successfully',
      };
    } catch (error: any) {
      console.error('Error deleting field:', error);
      throw new Error(error.response?.data?.message || error.message || 'Failed to delete field');
    }
  }
}

export const fieldDetailApiService = new FieldDetailApiService();
